import React, { useEffect, useState } from 'react';
import { Button, Checkbox, ConfigProvider, Form, Input, message } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import { acceptTeamMemberInvite, ERegisterType } from 'api/public';
import { saveStep } from 'api/ppaOnboarding';
import { EInvitedProviderStep } from 'pages/InvitedProviderOnboarding/steps';
import { EInviteRedirect, TAcceptInviteRes } from 'types/invite';
import {
    setUserToken,
    setScheduleToken,
    setUserId,
    setLocalStoreProviderTokenSetTime,
    setHasOnceSuccessLogin,
    increaseLoginCount,
} from 'utils/localstore';
import { setCookieProviderToken, setCookieProviderTokenSetTime } from 'utils/shareAccount';
import { setCurrentPlatformType } from 'utils/sessionStorage';
import ProviderStore from 'store/Provider';
import formStyles from 'pages/PPALoginPage/components/LoginForm/s.module.less';
import s from './s.module.less';

interface ActivateFormValues {
    email: string;
    firstName?: string;
    lastName?: string;
    password: string;
}

interface ActivateFormProps {
    providerName?: string;
    token?: string;
    initialEmail?: string;
    initialFirstName?: string;
    initialLastName?: string;
    isExistingUser?: boolean;
    emailLocked?: boolean;
    onSubmit?: (values: ActivateFormValues) => void;
}

export default function ActivateForm({
    providerName,
    token = '',
    initialEmail = '',
    initialFirstName = '',
    initialLastName = '',
    isExistingUser = false,
    emailLocked = false,
    onSubmit,
}: ActivateFormProps) {
    const [form] = Form.useForm<ActivateFormValues>();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [, setProviderData] = ProviderStore.useStore();
    const emailValue = Form.useWatch('email', form);
    const passwordValue = Form.useWatch('password', form);
    const agreementValue = Form.useWatch('agreement', form);

    useEffect(() => {
        const patch: Partial<ActivateFormValues> = {};
        if (initialEmail) patch.email = initialEmail;
        if (initialFirstName) patch.firstName = initialFirstName;
        if (initialLastName) patch.lastName = initialLastName;
        if (Object.keys(patch).length) {
            form.setFieldsValue(patch as ActivateFormValues);
        }
    }, [initialEmail, initialFirstName, initialLastName, form]);

    const persistAuth = (data: TAcceptInviteRes, email: string) => {
        // Mirror PPA onboarding/login auth persistence so subsequent calls succeed
        // and useTryToLoad/redirecToLogout does not bounce the user back to login.
        setProviderData('data', {
            id: data.userId,
            email,
            organizationId: data.organizationId,
            role: data.role,
            isAdmin: data.isAdmin,
        });
        setUserId(data.userId);
        increaseLoginCount();
        setUserToken(data.scheduleToken);
        setScheduleToken(data.scheduleToken);
        setHasOnceSuccessLogin('true');
        setCurrentPlatformType(ERegisterType.KLARITY_PORTAL);
        setLocalStoreProviderTokenSetTime(Date.now());
        try {
            setCookieProviderToken(data.scheduleToken);
            setCookieProviderTokenSetTime(Date.now());
        } catch (e) {
            // cookie failures are non-fatal for local dev
        }
    };

    const handleSubmit = async (values: ActivateFormValues) => {
        if (!token) {
            message.error('Invite token is missing. Please use the link from your invite email.');
            return;
        }
        try {
            setLoading(true);
            const res = await acceptTeamMemberInvite({
                token,
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                password: values.password,
            });
            const resWithCode = res as typeof res & { errorCode?: number };
            const payload = (res?.data as { data?: TAcceptInviteRes } | undefined)?.data;
            if (res.error || !payload) {
                if (resWithCode.errorCode === 4007) {
                    message.error('This invite link has already been used. Please sign in.');
                    navigate(PATH.PPA_LOGIN);
                    return;
                }
                message.error(res.error || 'Activation failed. Please try again.');
                return;
            }
            const data = payload;
            persistAuth(data, values.email);
            onSubmit?.(values);
            if (data.redirectTo === EInviteRedirect.ONBOARDING_PPA) {
                await saveStep(EInvitedProviderStep.CREATE_PROFILE_CREDENTIALS);
                navigate(PATH.INVITED_PROVIDER_ONBOARDING_CREATE_PROFILE);
            } else {
                navigate(`${PATH.DASHBOARD}/${PATH.PROVIDER_HOME}`);
            }
        } catch (e) {
            message.error('Activation failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={formStyles.formContainer}>
            <ConfigProvider theme={{ token: { colorPrimary: '#0E62F4' } }}>
                <Form
                    form={form}
                    onFinish={handleSubmit}
                    initialValues={{
                        email: initialEmail,
                        firstName: initialFirstName,
                        lastName: initialLastName,
                        password: '',
                    }}
                    layout="vertical"
                    requiredMark={false}
                    className={`${formStyles.loginForm} ${s.form}`}
                >
                    <div className={formStyles.formHeader}>
                        <h2 className={formStyles.formTitle}>
                            {isExistingUser ? 'Accept invitation' : 'Activate your account'}
                        </h2>
                        {providerName && (
                            <p className={formStyles.formSubtitle}>
                                {providerName} has invited you to join their practice.
                            </p>
                        )}
                    </div>

                    <div className={formStyles.formBody}>
                        <Form.Item
                            name="email"
                            label={
                                <span className={formStyles.inputLabel}>
                                    Email address <span className={formStyles.required}>*</span>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Please enter your email' },
                                { type: 'email', message: 'Please enter a valid email' },
                            ]}
                        >
                            <Input size="large" className={formStyles.input} disabled={emailLocked} />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            label={
                                <span className={formStyles.inputLabel}>
                                    Password <span className={formStyles.required}>*</span>
                                </span>
                            }
                            rules={[{ required: true, message: 'Please enter your password' }]}
                        >
                            <Input.Password
                                size="large"
                                className={formStyles.input}
                                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                            />
                        </Form.Item>
                    </div>

                    <div className={formStyles.formActions}>
                        <Form.Item
                            name="agreement"
                            valuePropName="checked"
                            initialValue={false}
                            rules={[
                                {
                                    validator: (_, value) =>
                                        value
                                            ? Promise.resolve()
                                            : Promise.reject(new Error('Please agree to the terms to continue')),
                                },
                            ]}
                        >
                            <Checkbox className={s.agreement}>
                                <span className={s.agreementText}>
                                    By checking this box, I agree to Klarity&apos;s{' '}
                                    <a
                                        href="https://www.helloklarity.com/terms-conditions"
                                        target="_blank"
                                        rel="noreferrer"
                                        className={s.link}
                                    >
                                        Terms of Services
                                    </a>{' '}
                                    and{' '}
                                    <a
                                        href="https://www.helloklarity.com/privacy-policy"
                                        target="_blank"
                                        rel="noreferrer"
                                        className={s.link}
                                    >
                                        Privacy Policy
                                    </a>
                                    .
                                </span>
                            </Checkbox>
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={loading}
                                disabled={!emailValue || !passwordValue || !agreementValue || !token}
                                className={formStyles.submitButton}
                                block
                            >
                                Accept your invitation
                            </Button>
                        </Form.Item>
                    </div>
                </Form>
            </ConfigProvider>
        </div>
    );
}
